import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  Linkedin, 
  Twitter, 
  Facebook, 
  Instagram,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  Star
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter`, { email });
      toast.success(response.data.message);
      setEmail("");
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const footerLinks = {
    Services: [
      { name: "Reputation Management", href: "/services#reputation" },
      { name: "Review Monitoring", href: "/services#monitoring" },
      { name: "Online Marketing", href: "/services#marketing" },
      { name: "AdSense Optimization", href: "/services#adsense" },
    ],
    Platforms: [
      { name: "Google Reviews", href: "/services" },
      { name: "Yelp", href: "/services" },
      { name: "Avvo", href: "/services" },
      { name: "Vitals & WebMD", href: "/services" },
    ],
    Company: [
      { name: "About Us", href: "/about" },
      { name: "Pricing", href: "/pricing" },
      { name: "Contact", href: "/contact" },
      { name: "Book Consultation", href: "/book-consultation" },
    ],
  };

  const socialLinks = [
    { name: "LinkedIn", icon: Linkedin, href: "#" },
    { name: "Twitter", icon: Twitter, href: "#" },
    { name: "Facebook", icon: Facebook, href: "#" },
    { name: "Instagram", icon: Instagram, href: "#" },
  ];

  const platforms = ["Google", "Yelp", "Avvo", "Vitals", "WebMD", "Zillow", "Facebook", "BBB"];

  return (
    <footer data-testid="footer" className="bg-[#1A1A1A] text-white relative">
      {/* Platforms Banner */}
      <div className="bg-[#0F0F0F] py-6 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-center text-sm text-gray-400 mb-4">PLATFORMS WE MONITOR & MANAGE</p>
          <div className="flex flex-wrap justify-center gap-6 lg:gap-10">
            {platforms.map((platform) => (
              <span key={platform} className="text-gray-500 font-medium text-sm">{platform}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-8">
        {/* Newsletter Section */}
        <div className="bg-gradient-to-r from-[#F37021]/20 to-[#F37021]/5 rounded-2xl p-8 lg:p-12 mb-12">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Get Reputation Tips
              </h3>
              <p className="text-gray-400">
                Expert advice on managing your online reputation delivered to your inbox.
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
                className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500 h-12"
              />
              <Button
                type="submit"
                disabled={isLoading}
                data-testid="newsletter-submit-btn"
                className="bg-[#F37021] hover:bg-[#D95F18] text-white h-12 px-6"
              >
                {isLoading ? "..." : "Subscribe"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12 mb-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img
                src="/logo-icon.jpg"
                alt="5 Star Marketing"
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-gray-400 text-sm mb-4">
              AI-powered reputation management. Protect and enhance your online presence.
            </p>
            <div className="flex items-center gap-1 mb-4">
              {[1,2,3,4,5].map((i) => (
                <Star key={i} className="h-4 w-4 fill-[#F37021] text-[#F37021]" />
              ))}
            </div>
            <div className="space-y-2 text-sm text-gray-400">
              <a href="mailto:info@5starmarketing.com" className="flex items-center gap-2 hover:text-[#F37021] transition-colors">
                <Mail className="h-4 w-4" />
                info@5starmarketing.com
              </a>
              <a href="tel:+19493541412" className="flex items-center gap-2 hover:text-[#F37021] transition-colors">
                <Phone className="h-4 w-4" />
                (949) 354-1412
              </a>
            </div>
          </div>

          {/* Link Columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="font-semibold text-white mb-4">{category}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-sm text-gray-400 hover:text-[#F37021] transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Certifications */}
        <div className="border-t border-white/10 pt-8 mb-8">
          <div className="flex flex-wrap justify-center gap-8 items-center opacity-60">
            <div className="text-center">
              <p className="text-xs text-gray-500">Certified</p>
              <p className="font-bold text-white">Google Platinum Partner</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Rating</p>
              <p className="font-bold text-white">BBB A+ Rated</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Powered by</p>
              <p className="font-bold text-white">AI Technology</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} 5 Star Marketing. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.href}
                data-testid={`social-${social.name.toLowerCase()}`}
                className="p-2 rounded-full bg-white/5 hover:bg-[#F37021]/20 hover:text-[#F37021] transition-colors"
                aria-label={social.name}
              >
                <social.icon className="h-5 w-5" />
              </a>
            ))}
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="#" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="#" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
